import React, { useState, useEffect } from 'react';
import { ref, update, get } from 'firebase/database';
import { db } from '../../firebase/config';
import './EditUserInfo.css';

const EditUserInfo = ({ user, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    phone: '',
    mobile: '',
    email: '',
    name: '',
    pan: '',
    aadhar: '',
    address: '',
    city: '',
    state: '',
    pin: '',
    bankDetails: {
      accountHolderName: '',
      accountNumber: '',
      accountType: 'savings',
      bankName: '',
      branchName: '',
      ifscCode: ''
    }
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (user) {
      setFormData({
        phone: user.phone || '',
        mobile: user.mobile || '',
        email: user.email || '',
        name: user.name || '',
        pan: user.pan || '',
        aadhar: user.aadhar || '',
        address: user.address || '',
        city: user.city || '',
        state: user.state || '',
        pin: user.pin || '',
        bankDetails: {
          accountHolderName: user.bankDetails?.accountHolderName || '',
          accountNumber: user.bankDetails?.accountNumber || '',
          accountType: user.bankDetails?.accountType || 'savings',
          bankName: user.bankDetails?.bankName || '',
          branchName: user.bankDetails?.branchName || '',
          ifscCode: user.bankDetails?.ifscCode || ''
        }
      });
    }
  }, [user]);

  const handleChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }

    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Phone validation
    if (!formData.phone || formData.phone.length !== 10) {
      newErrors.phone = 'Phone number must be 10 digits';
    }

    // PAN validation
    if (formData.pan && !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(formData.pan.toUpperCase())) {
      newErrors.pan = 'Invalid PAN format';
    }

    // Aadhar validation
    if (formData.aadhar && !/^\d{12}$/.test(formData.aadhar)) {
      newErrors.aadhar = 'Aadhar must be 12 digits';
    }

    // Bank details validation
    if (!formData.bankDetails.accountHolderName) {
      newErrors['bankDetails.accountHolderName'] = 'Account holder name is required';
    }
    if (!formData.bankDetails.accountNumber || formData.bankDetails.accountNumber.length < 9) {
      newErrors['bankDetails.accountNumber'] = 'Invalid account number';
    }
    if (!formData.bankDetails.ifscCode || !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(formData.bankDetails.ifscCode.toUpperCase())) {
      newErrors['bankDetails.ifscCode'] = 'Invalid IFSC code';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      let userRef;
      if (user.dataSource === 'trainers') {
        userRef = ref(db, `HTAMS/company/trainers/${user.uid}`);
      } else if (user.dataSource === 'employees') {
        userRef = ref(db, `HTAMS/company/Employees/${user.uid}`);
      } else {
        userRef = ref(db, `HTAMS/users/${user.uid}`);
      }

      // Update only editable fields
      const updateData = {
        phone: formData.phone,
        mobile: formData.mobile,
        email: formData.email,
        name: formData.name,
        pan: formData.pan.toUpperCase(),
        aadhar: formData.aadhar,
        address: formData.address,
        city: formData.city,
        state: formData.state,
        pin: formData.pin,
        bankDetails: {
          ...formData.bankDetails,
          verified: false, // Reset verification when bank details change
          verifiedAt: null
        },
        lastUpdated: Date.now()
      };

      await update(userRef, updateData);

      // Update document verification if PAN/Aadhar changed
      if (formData.pan !== user.pan || formData.aadhar !== user.aadhar) {
        const docVerifRef = ref(db, `HTAMS/users/${user.uid}/documentVerification`);
        const docSnapshot = await get(docVerifRef);

        if (docSnapshot.exists()) {
          const docData = docSnapshot.val();

          // Update PAN card document if changed
          if (formData.pan !== user.pan && docData.documents?.panCard) {
            await update(ref(db, `HTAMS/users/${user.uid}/documentVerification/documents/panCard/formData`), {
              panNumber: formData.pan.toUpperCase()
            });
            await update(ref(db, `HTAMS/users/${user.uid}/documentVerification/documents/panCard`), {
              verificationStatus: 'pending',
              reviewedAt: null,
              reviewedBy: null
            });
          }

          // Update Aadhar document if changed
          if (formData.aadhar !== user.aadhar && docData.documents?.aadharCard) {
            await update(ref(db, `HTAMS/users/${user.uid}/documentVerification/documents/aadharCard/formData`), {
              aadharNumber: formData.aadhar
            });
            await update(ref(db, `HTAMS/users/${user.uid}/documentVerification/documents/aadharCard`), {
              verificationStatus: 'pending',
              reviewedAt: null,
              reviewedBy: null
            });
          }
        }
      }

      onSave(updateData);
      alert('User information updated successfully!');
      onClose();
    } catch (error) {
      console.error('Error updating user:', error);
      alert('Failed to update user information. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const readOnlyFields = [
    { label: 'User ID', value: user.uid },
    { label: 'Role', value: user.role },
    { label: 'Current Level', value: user.currentLevel },
    { label: 'My Sales', value: user.MySales || '0' },
    { label: 'My Team', value: user.MyTeam || 'None' },
    { label: 'Referred By', value: user.referredBy || 'None' },
    { label: 'Email', value: user.email || 'N/A' },
    { label: 'PAN Card', value: user.pan || 'N/A' },
    { label: 'Address', value: user.address || 'N/A' },
    { label: 'City', value: user.city || 'N/A' },
    { label: 'State', value: user.state || 'N/A' },
    { label: 'PIN Code', value: user.pin || 'N/A' },
    { label: 'Document Verification Status', value: user.documentVerificationStatus || 'Not Started' },
    { label: 'Created At', value: new Date(user.createdAt).toLocaleDateString() },
    { label: 'Account Status', value: user.isActive ? 'Active' : 'Inactive' }
  ];

  return (
    <div className="edit-user-overlay">
      <div className="edit-user-modal">
        <div className="edit-user-header">
          <h2>Edit User Information</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit} className="edit-user-form">
          <div className="form-sections">
            {/* Read-only Information */}
            <div className="form-section">
              <h3>Account Information (Read-only)</h3>
              <div className="readonly-grid">
                {readOnlyFields.map((field, index) => (
                  <div key={index} className="readonly-field">
                    <label>{field.label}</label>
                    <span>{field.value}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Editable Personal Information */}
            <div className="form-section">
              <h3>Personal Information (Editable)</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label>Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Phone *</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleChange('phone', e.target.value.replace(/\D/g, ''))}
                    maxLength={10}
                    required
                  />
                  {errors.phone && <span className="error">{errors.phone}</span>}
                </div>

                <div className="form-group">
                  <label>Aadhar Number</label>
                  <input
                    type="text"
                    value={formData.aadhar}
                    onChange={(e) => handleChange('aadhar', e.target.value.replace(/\D/g, ''))}
                    maxLength={12}
                    placeholder="123456789012"
                  />
                  {errors.aadhar && <span className="error">{errors.aadhar}</span>}
                </div>
              </div>
            </div>

            {/* Bank Details */}
            <div className="form-section">
              <h3>Bank Details (Editable)</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label>Account Holder Name *</label>
                  <input
                    type="text"
                    value={formData.bankDetails.accountHolderName}
                    onChange={(e) => handleChange('bankDetails.accountHolderName', e.target.value)}
                    required
                  />
                  {errors['bankDetails.accountHolderName'] && (
                    <span className="error">{errors['bankDetails.accountHolderName']}</span>
                  )}
                </div>

                <div className="form-group">
                  <label>Account Number *</label>
                  <input
                    type="text"
                    value={formData.bankDetails.accountNumber}
                    onChange={(e) => handleChange('bankDetails.accountNumber', e.target.value)}
                    required
                  />
                  {errors['bankDetails.accountNumber'] && (
                    <span className="error">{errors['bankDetails.accountNumber']}</span>
                  )}
                </div>

                <div className="form-group">
                  <label>Account Type</label>
                  <select
                    value={formData.bankDetails.accountType}
                    onChange={(e) => handleChange('bankDetails.accountType', e.target.value)}
                  >
                    <option value="savings">Savings</option>
                    <option value="current">Current</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Bank Name *</label>
                  <input
                    type="text"
                    value={formData.bankDetails.bankName}
                    onChange={(e) => handleChange('bankDetails.bankName', e.target.value)}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Branch Name</label>
                  <input
                    type="text"
                    value={formData.bankDetails.branchName}
                    onChange={(e) => handleChange('bankDetails.branchName', e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label>IFSC Code *</label>
                  <input
                    type="text"
                    value={formData.bankDetails.ifscCode}
                    onChange={(e) => handleChange('bankDetails.ifscCode', e.target.value.toUpperCase())}
                    placeholder="ABCD0123456"
                    required
                  />
                  {errors['bankDetails.ifscCode'] && (
                    <span className="error">{errors['bankDetails.ifscCode']}</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button type="button" className="btn-cancel" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-save" disabled={loading}>
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditUserInfo;